import { api } from './api';
import type {
  LinkedInGenerateRequest,
  LinkedInGenerateResponse,
  LinkedInPostsResponse,
  LinkedInPatchRequest,
  LinkedInPatchResponse,
  LinkedInPostsQuery,
  LinkedInPreviewPromptResponse,
  LinkedInSuggestHooksRequest,
  LinkedInSuggestHooksResponse,
  LinkedInHookOption,
  LinkedInHookLibraryResponse,
  LinkedInFillHookRequest,
  LinkedInFillHookResponse,
  LinkedInSuggestAnglesRequest,
  LinkedInSuggestAnglesResponse,
  LinkedInBranchRequest,
  LinkedInBranchResponse,
  LinkedInRewriteRequest,
  LinkedInRewriteResponse,
  LinkedInLaunchSuggestRequest,
  LinkedInLaunchSuggestResponse,
  LinkedInLaunchGenerateRequest,
  LinkedInLaunchGenerateResponse,
  LinkedInLinkAdvocateRequest,
} from '../types/linkedin';

export async function generateLinkedInPost(
  req: LinkedInGenerateRequest
): Promise<LinkedInGenerateResponse> {
  return api.post<LinkedInGenerateResponse>('/linkedin/generate', req);
}

export async function suggestLinkedInHooks(
  req: LinkedInSuggestHooksRequest
): Promise<LinkedInSuggestHooksResponse> {
  return api.post<LinkedInSuggestHooksResponse>('/linkedin/suggest-hooks', req);
}

/** Browse the raw hook skeletons. FREE — no AI call happens server-side. */
export async function browseHookLibrary(params: {
  strategy_id: number;
  category?: string | null;
  q?: string;
  offset?: number;
  limit?: number;
}): Promise<LinkedInHookLibraryResponse> {
  const qs = new URLSearchParams({ strategy_id: String(params.strategy_id) });
  if (params.category) qs.set('category', params.category);
  if (params.q) qs.set('q', params.q);
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.limit) qs.set('limit', String(params.limit));
  return api.get<LinkedInHookLibraryResponse>(`/linkedin/hook-library?${qs.toString()}`);
}

/** Stored fills of one skeleton. Free. */
export async function listHookVariants(
  strategy_id: number, hook_id: string
): Promise<{ variants: LinkedInHookOption[] }> {
  const qs = new URLSearchParams({ strategy_id: String(strategy_id), hook_id });
  return api.get<{ variants: LinkedInHookOption[] }>(`/linkedin/hook-library/variants?${qs.toString()}`);
}

/** Fill ONE chosen skeleton. This is the only paid call in the library flow. */
export async function fillHookFromLibrary(
  req: LinkedInFillHookRequest
): Promise<LinkedInFillHookResponse> {
  return api.post<LinkedInFillHookResponse>('/linkedin/hook-library/fill', req);
}

export async function editHookVariant(
  cache_id: number, filled_text: string
): Promise<LinkedInHookOption> {
  return api.patch<LinkedInHookOption>(`/linkedin/hook-library/variants/${cache_id}`, { filled_text });
}

export async function deleteHookVariant(cache_id: number): Promise<{ ok: boolean }> {
  return api.delete<{ ok: boolean }>(`/linkedin/hook-library/variants/${cache_id}`);
}

export async function suggestLinkedInAngles(
  req: LinkedInSuggestAnglesRequest
): Promise<LinkedInSuggestAnglesResponse> {
  return api.post<LinkedInSuggestAnglesResponse>('/linkedin/suggest-angles', req);
}

export async function branchLinkedInPost(
  req: LinkedInBranchRequest
): Promise<LinkedInBranchResponse> {
  return api.post<LinkedInBranchResponse>('/linkedin/branch', req);
}

export async function rewriteLinkedInPost(
  req: LinkedInRewriteRequest
): Promise<LinkedInRewriteResponse> {
  return api.post<LinkedInRewriteResponse>('/linkedin/rewrite', req);
}

export async function previewLinkedInPrompt(
  req: LinkedInGenerateRequest
): Promise<LinkedInPreviewPromptResponse> {
  return api.post<LinkedInPreviewPromptResponse>('/linkedin/preview-prompt', req);
}

export async function fetchLinkedInPosts(
  query?: LinkedInPostsQuery
): Promise<LinkedInPostsResponse> {
  const params = new URLSearchParams();
  if (query?.strategy_id !== undefined) params.set('strategy_id', String(query.strategy_id));
  if (query?.status !== undefined) params.set('status', query.status);
  if (query?.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return api.get<LinkedInPostsResponse>(`/linkedin/posts${qs ? `?${qs}` : ''}`);
}

export async function patchLinkedInPost(
  id: number,
  patch: LinkedInPatchRequest
): Promise<LinkedInPatchResponse> {
  return api.patch<LinkedInPatchResponse>(`/linkedin/posts/${id}`, patch);
}

// --- Launch sequence (page cold-start) ---

export async function suggestLaunchSequence(
  req: LinkedInLaunchSuggestRequest
): Promise<LinkedInLaunchSuggestResponse> {
  return api.post<LinkedInLaunchSuggestResponse>('/linkedin/launch/suggest', req);
}

export async function generateLaunchSequence(
  req: LinkedInLaunchGenerateRequest
): Promise<LinkedInLaunchGenerateResponse> {
  return api.post<LinkedInLaunchGenerateResponse>('/linkedin/launch/generate', req);
}

export async function linkLaunchAdvocate(
  req: LinkedInLinkAdvocateRequest
): Promise<unknown> {
  return api.post<unknown>('/linkedin/launch/link', req);
}
